import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import {
  PencilIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { Stepper, Field, Select, Badge, Modal, useStepper, Spinner } from './ui'
import { uxLine } from '../utils/templateSummary'
import { buildDeployPayload } from '../utils/deployPayload'
import {
  renderNamingPattern,
  groupNameBuilders,
  generatedGroupNames,
  splitOnTokens,
  unresolvedTokens,
  effectiveNaming,
  withDeploymentVars,
  usesDeploymentName,
  deploymentNameProblem,
  deploymentLabel,
  FIRST_DEPLOYMENT_LABEL,
  DEPLOYMENT_NAME_MAX,
  INTUNE_NAME_MAX,
  FIRST_DEPLOYMENT,
  type DeploymentNaming,
} from '../utils/namingPattern'
import {
  AssignmentReviewTable,
  assignmentReviewRows,
  assignmentSummary,
  duplicateTargetProblems,
  INTENT_TITLE,
  toAssignmentGroup,
} from './AssignmentBlade'
import type { AssignmentRow, AssignmentGroup, GeneratedGroupName } from './AssignmentBlade'
import {
  diffSettings,
  diffSettingsComplete,
  flattenSettings,
  groupSettings,
  type FieldChange,
  type FlattenOptions,
} from '../utils/settingsView'
import { TEMPLATE_SETTING_LABELS } from '../utils/templateSettingLabels'
import {
  graphGroupLines,
  graphCreateAppLines,
  graphContentLines,
  graphAssignLines,
} from '../utils/graphPreview'
import { DevNote, DevScript, useDevMode } from './DevMode'
import type { Delivery } from '../utils/deployPayload'
import UxTemplateWizard from './UxTemplateWizard'
import DeploymentChanges from './DeploymentChanges'
import DeploymentTemplateWizard from './DeploymentTemplateWizard'
import {
  listDeploymentTemplates,
  listIntuneTemplates,
  startDeploymentJob,
  awaitDeploymentJob,
  DeploymentFollowEnded,
  listAzureADGroups,
  listAssignmentFilters,
  listIntuneAppCategories,
  DeploymentTemplate,
  IntuneDeploymentTemplate,
  BulkDeployResult,
  DeploymentJob,
  getRecommendedConfig,
  preflightDeploymentJob,
  listDeployments,
  fetchCurrentSettings,
  type AppCurrentSettings,
  type CurrentDeploymentSettings,
  type CurrentTemplateSetting,
  type DeploymentPreflightItem,
  type DeploymentTarget,
  type SharedGroup,
} from '../services/api'
import { errorText } from '../utils/errorText'
import { useNotificationStore } from '../store/notificationStore'
import { useTaskListStore } from '../store/taskListStore'

/**
 * Deploy wizard — the mock's `DeployWizard` (sap-mockup/components/DeployWizard.tsx), ported.
 *
 * The shape of the flow is the point: a deployment is TEMPLATES APPLIED TO PACKAGES, so the wizard
 * picks one of each and shows what they resolve to, rather than asking an admin to re-answer the
 * same twenty questions per rollout. Four steps, as in the mock: which apps, the user experience,
 * the Intune delivery, then an overview before anything is published.
 *
 * "Adjust" is the mock's apply mode (spec §7a.1): it opens the SAME editor the template uses and
 * hands its settings back HERE. The template is never rewritten, so every other app on it keeps
 * its settings. We carry the result as `inline_config` / `intune_inline_config` on the publish
 * payload, which is what makes the override a COPY rather than a reference (spec §7a).
 *
 * Branding is deliberately absent, as in the mock: a branding override is assigned to users and
 * devices independently of any deployment, so there is nothing to choose here.
 *
 * The flow matches the mock: a modal over the page it was launched from, template-only on both
 * picker steps, and Adjust opening the template's own editor as a second dialog on top. There is
 * no ad-hoc audience here — the audience is always something a deployment template stated, which
 * is also what keeps an empty target list from reaching the backend, where `[]` is falsy and
 * `required` falls through to ALL DEVICES.
 *
 * ONE DEPARTURE, deliberate: DEVELOPER MODE IS REAL. The mock's Graph column is captioned
 * "Indicative — the mock does not call Graph". Ours does, so every call is generated from the code
 * that issues it (see utils/graphPreview.ts) rather than written to look plausible.
 */

export interface DeployApp {
  id: string
  name: string
  kind: 'private' | 'public'
  /** Needed to RESOLVE naming patterns. The mock's DeployTarget carries publisher and version for
   *  exactly this reason — without them {Version}/{Publisher}/{Arch} stay as tokens and the
   *  Overview shows a pattern where a name belongs. Optional so a caller that genuinely does not
   *  know them still compiles; the token is then left visible rather than faked. */
  publisher?: string
  version?: string
  arch?: string
  /** Set when no new deployment of the package may be made (a catalog entry that was replaced):
   *  why, shown in place of the New deployment choice. Its existing deployments can still be
   *  updated. */
  newDeploymentBlocked?: string
  /** The package was published anyway, without a passing test (Needs your decision). */
  notFullyTested?: boolean
}

interface Props {
  isOpen: boolean
  onClose: () => void
  apps: DeployApp[]
  /** The deployment job was recorded and a worker owns it. The wizard has closed by the time this
   *  fires, so this is where a caller drops whatever the wizard was working on — a catalog
   *  selection is spent once it has been handed to a worker, and leaving it ticked invites the
   *  same apps being deployed twice. */
  onStarted?: (job: DeploymentJob) => void
  /** A deployment finished, minutes later. REPORTING ONLY, for the caller's lists — the outcome
   *  itself is reported by the banner, which outlives the wizard. It fires on failure too, so that
   *  whatever published still reaches the caller's list. */
  onDeployed?: (r: BulkDeployResult) => void
  /** Which deployment of a single package the wizard opens on: a new one, or the existing one
   *  with this uid (null is the package's first deployment). Omitted, it opens on updating the
   *  first deployment. Ignored when the package has no deployment yet. */
  initialTarget?: WizardTarget
}

/** The wizard's choice of deployment, before it becomes the request's `deployment` field. */
export type WizardTarget = { action: 'new' } | { action: 'update'; uid: string | null }

const STEPS = [
  { key: 'apps', label: 'Apps' },
  { key: 'experience', label: 'User experience' },
  { key: 'deployment', label: 'Deployment' },
  { key: 'overview', label: 'Overview' },
] as const

/**
 * The reference's own classes, not an approximation of them.
 *
 * These were hand-rolled utility strings — my reading of what `btn-outline btn-sm` looks like —
 * and that is precisely how a screen ends up structurally identical to the reference and visually
 * a different product. `.btn-*`, `.label` and the palette now live in index.css, ported from
 * sap-mockup/app/globals.css, so the reference's class names work here verbatim.
 */
const btnOutline = 'btn-outline'
const btnOutlineSm = 'btn-outline btn-sm'
const btnPrimary = 'btn-blue'

const noteCls = 'rounded-lg border border-line bg-panel3 px-4 py-2.5 text-[12px] text-fmuted'

const labelCls = 'label'

// ---------------------------------------------------------------- step 1
/**
 * Which account the installer runs as, for ONE package.
 *
 * Intune calls it installExperience.runAsAccount, and it has to match what the installer
 * actually does: an app that writes to %LOCALAPPDATA% (Squirrel, Electron, BitRock) cannot
 * usefully run as system, and an MSI targeting Program Files must. Publishing the wrong one
 * fails as 0x87D1041C, "not detected after installation" — the installer appears to succeed and
 * detection never matches.
 *
 * So the default is what the packager detected, and the reasons it decided that are shown: an
 * override should be an informed one.
 */
const InstallContextField: React.FC<{
  appId: string
  value: string
  onChange: (v: string) => void
  /** The app may already be deployed and the check is still running: the deployed install
   *  context, if any, cannot be changed, so nothing is offered until the answer is in. */
  checking?: boolean
}> = ({ appId, value, onChange, checking = false }) => {
  const q = useQuery({
    queryKey: ['recommended-config', appId],
    queryFn: () => getRecommendedConfig(appId),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
  const detected = q.data?.baseline?.install_scope
  const reasons = q.data?.baseline?.install_scope_reasons
  const detectedLabel = detected ? detected.charAt(0).toUpperCase() + detected.slice(1) : ''

  if (checking) {
    return (
      <div className="mt-2">
        <p className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
          Install context
        </p>
        <p className="flex items-center gap-2 text-[11px] text-theme-text-tertiary">
          <Spinner className="h-3.5 w-3.5" /> Checking the existing deployment…
        </p>
      </div>
    )
  }
  return (
    <div className="mt-2">
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
        Install context
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={checking}
        className="rounded-lg disabled:opacity-60 border border-theme-border-primary bg-theme-primary px-2 py-1 text-xs text-theme-text-primary"
      >
        <option value="">AI-detected{detectedLabel ? ` — ${detectedLabel}` : ''}</option>
        <option value="system">System (device)</option>
        <option value="user">User</option>
      </select>
      {detected && (
        <p className="mt-1 text-[11px] text-theme-text-tertiary">
          The AI read the installer and detected a{' '}
          <span className="font-medium">{detectedLabel}</span> install
          {Array.isArray(reasons) && reasons.length > 0 && <> ({reasons.join('; ')})</>}. Choose
          System or User above to override it.
        </p>
      )}
    </div>
  )
}

const CONTEXT_LABEL: Record<string, string> = { system: 'System (device)', user: 'User' }

const contextLabel = (ctx?: string | null) => (ctx ? CONTEXT_LABEL[ctx] ?? ctx : 'AI-detected')

/** The packager's install_scope as an install context: machine (or system) is System. */
const SCOPE_CONTEXT: Record<string, string> = { machine: 'System', system: 'System', user: 'User' }

/** The install context a new deployment is created with, as the Overview shows it: the one
 *  chosen in the wizard, else the one the packager detected, saying which. */
export function overviewContextLabel(chosen?: string | null, detected?: string | null): string {
  if (chosen) return `${SCOPE_CONTEXT[chosen] ?? chosen} (chosen)`
  if (detected) return `${SCOPE_CONTEXT[detected] ?? detected} (AI-detected)`
  return 'AI-detected'
}

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : 'an earlier date')

/** "Already deployed as X on date, as System (device)." */
function deployedAs(i: DeploymentPreflightItem): string {
  const name = i.display_name ?? i.rendered_name ?? i.name ?? 'this app'
  const ctx = i.install_context ? `, as ${contextLabel(i.install_context)}` : ''
  return `Already deployed as ${name} on ${when(i.published_at)}${ctx}.`
}

const OBJECT_LABEL: Record<string, string> = { install: 'install app', update_only: 'update-only app' }

/** What an in-place update adds and leaves, in the words the confirmation shows. */
function noticeTexts(i: DeploymentPreflightItem): string[] {
  const added = (i.creates ?? []).map(
    (r) => `This template publishes an ${OBJECT_LABEL[r] ?? r} the deployment does not have yet; Deploy adds it to the deployment.`,
  )
  const kept = (i.notices ?? []).map((n) => {
    if (n === 'update_only_not_removed') {
      return 'This deployment has an update-only app this template does not publish; it keeps the name and assignments it has. Remove it from the deployments list to delete it.'
    }
    if (n === 'install_not_removed') {
      return 'This deployment has an install app this template does not publish (Skip install is on); it keeps the name and assignments it has. Remove it from the deployments list to delete it.'
    }
    return String(n)
  })
  return [...added, ...kept]
}

/** Why a refused item stops the deployment, naming the app in the way. `deploymentsUnread`: the
 *  wizard deploys one package, which offers the deployment choice, but could not read the
 *  package's deployments, so it named none. */
function refusalText(i: DeploymentPreflightItem, deploymentsUnread = false): string {
  const who = i.name ?? i.app_id
  if (i.reason === 'name_taken') {
    const t = i.taken_by
    // `other_deployment_name` is present when another deployment of this same package holds it.
    const holder =
      t && t.other_deployment_name !== undefined
        ? ` in the deployment ${deploymentLabel(t.other_deployment_name, who)} of this package`
        : t?.deployment_name
          ? ` as the deployment of ${t.deployment_name}`
          : ''
    return `${who}: an app named ${t?.display_name ?? i.rendered_name} with this version already exists in Intune${holder}. Change the naming pattern in the deployment template, or remove that deployment first.`
  }
  if (i.reason === 'install_context') {
    return `${who}: deployed as ${contextLabel(i.install_context)}; the install context of a published app is not changed in place. Remove the deployment and deploy again, or create a new deployment, to change it.`
  }
  if (i.reason === 'choose_deployment') {
    if (deploymentsUnread) {
      return `${who}: the deployments of this package could not be read, so none could be chosen. Close this dialog and try again.`
    }
    return `${who}: this package has more than one deployment; deploy it from its package page to choose the deployment.`
  }
  if (i.reason === 'deployment_not_found') {
    return `${who}: the deployment to update no longer exists. Close this dialog and open it again from the package page.`
  }
  if (i.reason === 'deployment_name_required') {
    return `${who}: name the new deployment; the package already has one.`
  }
  if (i.reason === 'deployment_name_taken') {
    return `${who}: another deployment of this package is named ${i.deployment_name ?? 'that'}. Choose another name.`
  }
  if (i.reason === 'deployment_name_reserved') {
    return `${who}: ${FIRST_DEPLOYMENT_LABEL} is kept for the package's first deployment. Choose another name.`
  }
  if (i.reason === 'deployment_name_invalid') {
    return `${who}: the deployment name can use letters, digits, spaces, dots, hyphens and underscores, up to ${DEPLOYMENT_NAME_MAX} characters.`
  }
  if (i.reason === 'deployment_name_too_long') {
    return `${who}: with the deployment name, an app or group name is longer than the ${INTUNE_NAME_MAX} characters Intune and Entra allow. Choose a shorter name.`
  }
  if (i.reason === 'deployment_name_clash') {
    const other = i.name_clash?.[0]
    return `${who}: ${other?.name ?? 'a name'} is used by the deployment ${deploymentLabel(other?.other_deployment_name)} even with the deployment name added. Choose templates whose names differ from that deployment's.`
  }
  return `${who}: cannot be deployed.`
}

/** Why a deployed app publishes as a new app rather than updating in place. */
function publishNote(i: DeploymentPreflightItem): string | null {
  if (i.reason === 'package_changed') {
    return `${i.name ?? i.app_id}: the package changed since it was deployed, so a new app will supersede the deployed one.`
  }
  if (i.reason === 'app_missing') {
    return `${i.name ?? i.app_id}: the deployed app is no longer in Intune, so a new app will be published.`
  }
  return null
}

/** A naming pattern listed as written, when it fills in to the same names as the template's. */
const PATTERN_LABELS: Record<string, string> = {
  'naming.appPattern': 'App name pattern',
  'naming.updateAppPattern': 'Update-only app name pattern',
  'naming.groupPattern': 'Security group pattern',
  'naming.updateGroupPattern': 'Update-only group pattern',
}

// ---------------------------------------------------------------- preselection
/**
 * The picker entry for a deployment whose template was deleted: the settings the deployment has
 * now, under the deleted template's name. Never sent as a template id (sentTemplateId).
 */
export const KEPT_ID = 'deployment-own-settings'

/** What the picker calls the deployment's own settings once its template is gone. */
export function keptLabel(name: string | null): string {
  return name ? `${name} (deleted)` : 'This deployment’s settings (template deleted)'
}

function deletedNote(name: string | null): string {
  return `The template this deployment used${name ? `, ${name},` : ''} was deleted. The deployment keeps its own settings, and the Overview compares with them.`
}
const MIXED_NOTE =
  'These apps use different templates now; the Overview lists what changes for each.'
const NOT_RECORDED_NOTE =
  'The adjusted settings of this deployment were not recorded, so the default template is selected; the Overview lists what changes.'

/** The template id a request names: none for the deployment's own settings. */
export function sentTemplateId(id: string | null): string | null {
  return id === KEPT_ID ? null : id
}

/** What one picker opens on. A null id leaves the tenant default to be chosen. */
interface KindPick {
  id: string | null
  /** Seeded into Adjust: the settings the deployment carries. */
  override: any | null
  /** The template marked "(current)" in the picker. */
  currentId: string | null
  note: string | null
  /** The template was deleted: the deployment's own settings, offered as KEPT_ID. */
  kept?: { name: string | null; config: any }
}

const NO_PICK: KindPick = { id: null, override: null, currentId: null, note: null }

function keptPick(name: string | null, config: any): KindPick {
  return {
    id: KEPT_ID,
    override: null,
    currentId: KEPT_ID,
    note: deletedNote(name),
    kept: { name, config },
  }
}

/** The picker's starting point for one template kind of a deployment. */
function pickFor(s: CurrentTemplateSetting | null | undefined, templates: { id: string }[]): KindPick {
  if (!s) return NO_PICK
  const listed = (id: string | null) => !!id && templates.some((t) => t.id === id)
  if (s.adjusted) {
    if (s.config && s.exists !== false && listed(s.adjusted_from)) {
      return { id: s.adjusted_from, override: s.config, currentId: s.adjusted_from, note: null }
    }
    // The base is gone: the deployment keeps its adjusted settings, compared with themselves.
    if (s.config) return keptPick(s.name ?? null, s.config)
    return { ...NO_PICK, note: NOT_RECORDED_NOTE }
  }
  if (!s.template_id) return NO_PICK
  if (s.exists !== false && listed(s.template_id)) {
    return { ...NO_PICK, id: s.template_id, currentId: s.template_id }
  }
  return s.config ? keptPick(s.name ?? null, s.config) : NO_PICK
}

/**
 * Where both pickers open, from the deployments' current settings. `appCount` is the number of
 * apps in the wizard, deployed or not. A bulk deploy (more than one app) preselects only when
 * every deployed app uses the same pair of templates and none was adjusted, and never seeds
 * Adjust: one app's adjustment would otherwise be applied to every app. Otherwise the tenant
 * default is chosen and the Overview lists what changes for each app.
 */
export function planPreselection(
  current: AppCurrentSettings[],
  uxTemplates: { id: string }[],
  depTemplates: { id: string }[],
  appCount: number,
  target?: WizardTarget,
): { ux: KindPick; dep: KindPick } {
  // A new deployment starts from the tenant default; an update starts from the chosen
  // deployment's own settings.
  if (target?.action === 'new') return { ux: NO_PICK, dep: NO_PICK }
  const deployed = current
    .map((a) => chosenDeployment(a.deployments, target))
    .filter((d): d is NonNullable<typeof d> => !!d)
  if (!deployed.length) return { ux: NO_PICK, dep: NO_PICK }
  const plan = {
    ux: pickFor(deployed[0].ux, uxTemplates),
    dep: pickFor(deployed[0].delivery, depTemplates),
  }
  if (appCount <= 1) return plan
  const pair = (d: (typeof deployed)[number]) =>
    `${d.ux?.template_id ?? ''}|${d.delivery?.template_id ?? ''}`
  const same = deployed.every((d) => pair(d) === pair(deployed[0]))
  const adjusted = deployed.some((d) => d.ux?.adjusted || d.delivery?.adjusted)
  const seeds =
    plan.ux.override != null || plan.dep.override != null || !!plan.ux.kept || !!plan.dep.kept
  if (!same || adjusted || seeds) {
    const mixed = { ...NO_PICK, note: MIXED_NOTE }
    return { ux: mixed, dep: mixed }
  }
  return plan
}

/** The deployment an update targets: the one with `uid`, else the package's first deployment. */
function chosenDeployment(
  deployments: CurrentDeploymentSettings[] | undefined,
  target?: WizardTarget,
): CurrentDeploymentSettings | undefined {
  const list = deployments ?? []
  if (target?.action === 'update') {
    const match = list.find((d) => d.deployment_uid === target.uid)
    if (match) return match
  }
  return list[0]
}

const INTENT_WORD: Record<string, string> = {
  required: 'Required',
  available: 'Available',
  uninstall: 'Uninstall',
}

/** "Required (update-only app)": an intent, naming the app object when it is the update-only one. */
const intentOf = (intent: string, role: string) =>
  `${INTENT_WORD[intent] ?? intent}${role === 'update_only' ? ' (update-only app)' : ''}`

/** The warning for a group another deployment of the same package also assigns. */
export function sharedGroupText(g: SharedGroup, packageName: string, thisLabel: string): string {
  const other = deploymentLabel(g.other_deployment_name, packageName)
  // All devices and All users come back as groups named by their target, with no group id.
  const who =
    !g.group_id && g.group === 'All devices'
      ? 'Every device receives'
      : !g.group_id && g.group === 'All users'
        ? 'The devices of every user receive'
        : 'Devices in that group receive'
  const avoid =
    !g.group_id && (g.group === 'All devices' || g.group === 'All users')
      ? ` To avoid this, give each deployment its own audience: assign an Entra group instead of ${g.group} in one of them, or add an assignment filter (for example by device name or model) so each device matches only one deployment.`
      : ' To avoid this, choose another group for one of the deployments, or add an assignment filter so each device matches only one deployment.'
  return `${g.group} is assigned by this deployment (${thisLabel}) as ${intentOf(g.intent, g.role)} and by the deployment ${other} as ${intentOf(g.other_intent, g.other_role)}. ${who} both deployments of the app, which share detection rules, so one can undo the other.${avoid}`
}

/** What the Overview says about a new deployment's name. `added`: the name is added to every
 *  Intune name; `renamed` lists each name that clashes with another deployment of the package and
 *  the name it gets with the deployment name added. */
interface NameNote {
  text: string
  added: boolean
  renamed: { from: string; to: string }[]
  /** The deployment whose names clash, as the portal labels it. */
  other: string
}

/** A name clash is resolved by adding the deployment name to every name (the default), or by the
 *  administrator changing the name patterns; Deploy waits while "change" is chosen. */
type ClashChoice = 'add' | 'change'

/** The backend's answer to "what does starting this deployment do", as the steps read it. */
interface PreflightView {
  items: DeploymentPreflightItem[]
  loading: boolean
  error: string | null
}

/**
 * Update one of the package's deployments, or create another one with its own Intune apps,
 * assignments and install context. Shown only when the package already has a deployment.
 */
function DeploymentChoice({
  packageName,
  deployments,
  target,
  onTarget,
  newName,
  onNewName,
  nameProblem,
  newBlocked,
}: {
  packageName: string
  deployments: CurrentDeploymentSettings[]
  target: WizardTarget
  onTarget: (t: WizardTarget) => void
  newName: string
  onNewName: (v: string) => void
  nameProblem: string | null
  /** Why no new deployment may be made; the New deployment choice is then not offered. */
  newBlocked?: string
}) {
  const label = (d: CurrentDeploymentSettings) => deploymentLabel(d.deployment_name, packageName)
  const updateUid =
    target.action === 'update' ? target.uid : (deployments[0]?.deployment_uid ?? null)
  return (
    <fieldset className="mb-4 rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-3">
      <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
        Deployment
      </legend>
      <label className="flex items-start gap-2 text-sm text-theme-text-primary">
        <input
          type="radio"
          name="deployment-target"
          aria-label="Update an existing deployment"
          className="mt-1"
          checked={target.action === 'update'}
          onChange={() => onTarget({ action: 'update', uid: updateUid })}
        />
        <span className="min-w-0 flex-1">
          <span className="font-medium">Update an existing deployment</span>
          <span className="block text-[12px] text-theme-text-secondary">
            The chosen templates are applied to that deployment's Intune apps; no second app is
            created.
          </span>
        </span>
      </label>
      {target.action === 'update' && (
        <div className="ml-6 mt-2">
          {deployments.length > 1 ? (
            <select
              aria-label="Deployment to update"
              value={target.uid ?? ''}
              onChange={(e) =>
                onTarget({ action: 'update', uid: e.target.value === '' ? null : e.target.value })
              }
              className="rounded-lg border border-theme-border-primary bg-theme-primary px-2 py-1 text-xs text-theme-text-primary"
            >
              {deployments.map((d) => (
                <option key={d.deployment_uid ?? ''} value={d.deployment_uid ?? ''}>
                  {label(d)}
                </option>
              ))}
            </select>
          ) : (
            <span className="block text-[12px] text-theme-text-primary">
              {deployments[0] ? label(deployments[0]) : packageName}
            </span>
          )}
        </div>
      )}
      {newBlocked ? (
        <p className="mt-3 text-[12px] text-theme-text-secondary">{newBlocked}</p>
      ) : (
      <label className="mt-3 flex items-start gap-2 text-sm text-theme-text-primary">
        <input
          type="radio"
          name="deployment-target"
          aria-label="New deployment"
          className="mt-1"
          checked={target.action === 'new'}
          onChange={() => onTarget({ action: 'new' })}
        />
        <span className="min-w-0 flex-1">
          <span className="font-medium">New deployment</span>
          <span className="block text-[12px] text-theme-text-secondary">
            Publishes another Intune app for this package, with its own user experience,
            assignments and install context.
          </span>
        </span>
      </label>
      )}
      {target.action === 'new' && !newBlocked && (
        <div className="ml-6 mt-2">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
            Deployment name
          </label>
          <input
            type="text"
            aria-label="Deployment name"
            value={newName}
            onChange={(e) => onNewName(e.target.value)}
            placeholder="e.g. AVD"
            className="w-full max-w-xs rounded-lg border border-theme-border-primary bg-theme-primary px-2 py-1 text-sm text-theme-text-primary"
          />
          {nameProblem ? (
            <p className="mt-1 text-[12px] text-red-500">{nameProblem}</p>
          ) : (
            <p className="mt-1 text-[12px] text-theme-text-tertiary">
              Tells the deployments of this package apart in Agentic Packr. It appears in Intune
              names where your templates use <span className="font-mono">{'{DeploymentName}'}</span>
              . When the templates would give the same names as another deployment of this package,
              the Overview asks whether to add it to the end of every name or to change the names.
              Up to{' '}
              {DEPLOYMENT_NAME_MAX} characters; it cannot be changed later.
            </p>
          )}
        </div>
      )}
    </fieldset>
  )
}

/** The name of a package's first deployment, asked when the package has no deployment yet. */
function FirstDeploymentName({
  name,
  onName,
  problem,
}: {
  name: string
  onName: (v: string) => void
  problem: string | null
}) {
  return (
    <fieldset className="mb-4 rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-3">
      <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
        Deployment
      </legend>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
        Deployment name
      </label>
      <input
        type="text"
        aria-label="Deployment name"
        value={name}
        onChange={(e) => onName(e.target.value)}
        className="w-full max-w-xs rounded-lg border border-theme-border-primary bg-theme-primary px-2 py-1 text-sm text-theme-text-primary"
      />
      {problem ? (
        <p className="mt-1 text-[12px] text-red-500">{problem}</p>
      ) : (
        <p className="mt-1 text-[12px] text-theme-text-tertiary">
          The package&apos;s first deployment. Its name tells the deployments of this package apart
          in Agentic Packr; it does not appear in its Intune app and group names, where{' '}
          <span className="font-mono">{'{DeploymentName}'}</span> stays empty for the first
          deployment. Up to {DEPLOYMENT_NAME_MAX} characters; it cannot be changed later.
        </p>
      )}
    </fieldset>
  )
}

/** The deploy dialog's choices, kept for the browser session (see the draft effects). */
interface WizardDraft {
  at: number
  chosen: WizardTarget | null
  newName: string
  /** Missing in a draft kept before the first deployment was named. */
  firstName?: string
  uxId: string | null
  depId: string | null
  uxOverride: any | null
  depOverride: any | null
  installContexts: Record<string, string>
  stepIdx: number
}

/** A draft older than this is not restored. */
const DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000

function readDraft(key: string): WizardDraft | null {
  try {
    const raw = window.sessionStorage.getItem(key)
    const draft = raw ? (JSON.parse(raw) as WizardDraft) : null
    return draft && Date.now() - draft.at < DRAFT_MAX_AGE_MS ? draft : null
  } catch {
    return null
  }
}

function writeDraft(key: string, draft: WizardDraft) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(draft))
  } catch {
    /* storage unavailable: the dialog works without a draft */
  }
}

function clearDraft(key: string) {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}

/** What an in-place update changes about the deployed apps' names and groups, or null. */
function changesNames(i: DeploymentPreflightItem): string | null {
  const parts: string[] = []
  if (i.display_name && i.rendered_name && i.display_name !== i.rendered_name) {
    parts.push(`the app '${i.display_name}' is renamed '${i.rendered_name}'`)
  }
  const assignments: any = (i.changes as any)?.assignments ?? {}
  const changed = Object.values(assignments).some(
    (a: any) => (a?.added?.length ?? 0) > 0 || (a?.removed?.length ?? 0) > 0,
  )
  if (changed) parts.push('its assignments and groups change')
  return parts.length ? parts.join(', and ') : null
}

function AppsStep({
  apps,
  installContexts,
  onInstallContextChange,
  deployed,
  checking,
  choice,
  recordedContext = {},
}: {
  apps: DeployApp[]
  installContexts: Record<string, string>
  onInstallContextChange: (appId: string, value: string) => void
  /** Per app id, the preflight item that names the install context the app is deployed with. */
  deployed: Record<string, DeploymentPreflightItem>
  /** Whether the app's existing deployment is still being checked. */
  checking: (appId: string) => boolean
  /** The update-or-new choice, when the package already has a deployment. */
  choice?: React.ReactNode
  /** Per app id, the install context of the deployment being updated, from its record. */
  recordedContext?: Record<string, string | null | undefined>
}) {
  return (
    <div>
      {choice}
      <p className={`mb-3 ${noteCls}`}>
        {apps.length === 1
          ? 'The package is deployed as it was built — the templates decide the experience and the targeting, not the contents.'
          : `These ${apps.length} packages get the same experience and targeting. Deploy them separately if they need different settings.`}
      </p>
      <DevNote
        setting="One win32LobApp per package — this step picks which, nothing else"
        example="The .intunewin is already built; publishing uploads it as a contentVersion and commits the file."
      />
      <div className="space-y-2">
        {apps.map((a) => (
          <div
            key={a.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-theme-text-primary">{a.name}</p>
              <p className="truncate text-[12px] text-theme-text-secondary">{a.publisher || '—'}</p>
              {!deployed[a.id] && recordedContext[a.id] ? (
                <div className="mt-2">
                  <p className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
                    Install context
                  </p>
                  <p className="text-xs text-theme-text-primary">
                    {contextLabel(recordedContext[a.id] ?? null)} (as deployed)
                  </p>
                  <p className="mt-1 text-[11px] text-theme-text-tertiary">
                    A published app's install context is not changed in place; to change it,
                    remove the deployment and deploy again.
                  </p>
                </div>
              ) : deployed[a.id] ? (
                /* The running account of a published app is not changed by an in-place update,
                   so the deployed one is shown rather than offered. */
                <div className="mt-2">
                  <p className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-theme-text-tertiary">
                    Install context
                  </p>
                  <p className="text-xs text-theme-text-primary">
                    {contextLabel(deployed[a.id].install_context)} (as deployed)
                  </p>
                  <p className="mt-1 text-[11px] text-theme-text-tertiary">
                    {deployedAs(deployed[a.id])} A published app's install context is not changed
                    in place; to change it, remove the deployment and deploy again.
                  </p>
                </div>
              ) : (
                <InstallContextField
                  appId={a.id}
                  value={installContexts[a.id] ?? ''}
                  onChange={(v) => onInstallContextChange(a.id, v)}
                  checking={checking(a.id)}
                />
              )}
            </div>
            <span className="shrink-0 font-mono text-[12px] text-theme-text-tertiary">
              {a.version ? `v${a.version}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- steps 2 & 3
function PickStep({
  label,
  blurb,
  templates,
  selectedId,
  onSelect,
  resolved,
  edited,
  onAdjust,
  onReset,
  adjustNote,
  currentId,
  notice,
  devSetting,
  devExample,
}: {
  label: string
  blurb: string
  templates: { id: string; name: string }[]
  /** The template the existing deployment uses, marked "(current)". */
  currentId?: string | null
  /** Why the picker did not open on the deployment's own template. */
  notice?: string | null
  selectedId: string | null
  onSelect: (id: string) => void
  resolved: string
  edited: boolean
  /** Omitted where apply mode has nowhere to send the result. */
  onAdjust?: () => void
  onReset?: () => void
  adjustNote?: string
  /** What this template becomes on the Intune side, for dev mode. */
  devSetting: string
  devExample?: string
}) {
  return (
    <div className="space-y-4">
      <Field label={label} hint={blurb}>
        <Select
          value={selectedId ?? ''}
          onChange={onSelect}
          options={[
            ...(selectedId
              ? []
              : [{ value: '', label: templates.length ? 'Choose a template…' : 'No templates yet' }]),
            ...templates.map((t) => ({
              value: t.id,
              label: t.id === currentId ? `${t.name} (current)` : t.name,
            })),
          ]}
        />
      </Field>

      {notice && <p className={noteCls}>{notice}</p>}

      {selectedId && (
        <div className="rounded-xl border border-theme-border-primary bg-theme-tertiary p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-theme-text-primary">This deployment</p>
                {edited && <Badge tone="amber">Adjusted</Badge>}
              </div>
              <p className="mt-0.5 text-[13px] text-theme-text-secondary">{resolved}</p>
            </div>
            {onAdjust && (
              <div className="flex shrink-0 items-center gap-1">
                {edited && onReset && (
                  <button
                    type="button"
                    className={btnOutlineSm}
                    onClick={onReset}
                    title="Discard the adjustments and go back to the template"
                  >
                    <ArrowUturnLeftIcon className="h-3.5 w-3.5" /> Reset
                  </button>
                )}
                <button type="button" className={btnOutlineSm} onClick={onAdjust}>
                  <PencilIcon className="h-3.5 w-3.5" /> Adjust
                </button>
              </div>
            )}
          </div>

          <p className="mt-3 text-[12px] text-theme-text-tertiary">
            {adjustNote ??
              (edited
                ? 'Adjusted for this deployment only. The template is unchanged, and every other app using it keeps its current settings.'
                : 'Adjust opens the full editor. Anything you change applies to this deployment only — the template is never rewritten.')}
          </p>

          <DevNote
            setting={devSetting}
            example={
              devExample ??
              (edited
                ? 'The deployment stores a COPY of the resolved settings. No PATCH is issued against the template.'
                : 'The deployment stores a COPY of the template’s settings at publish time, not a reference to it.')
            }
          />
        </div>
      )}
    </div>
  )
}
// ---------------------------------------------------------------- overview parts
/**
 * A generated name, with any token that could not be resolved shown as a token rather than as
 * part of the name. Left plain, `sg-intune-sap-7-Zip-{Arch}-Required` reads as the name that will
 * be created, and the admin is being asked to confirm something untrue.
 */
function PatternText({ text }: { text: string }) {
  return (
    <>
      {splitOnTokens(text).map((part, i) =>
        part.unresolved ? (
          <span
            key={i}
            className="italic text-theme-text-tertiary"
            title="Not known until the app is packaged; resolved then."
          >
            {part.text}
          </span>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  )
}

function Line({ k, v, mono, badge }: { k: string; v: React.ReactNode; mono?: boolean; badge?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-theme-border-primary pb-2 last:border-0">
      <dt className="shrink-0 text-theme-text-secondary">{k}</dt>
      <dd
        className={`flex items-center gap-2 text-right font-medium text-theme-text-primary ${
          mono ? 'font-mono text-[13px]' : ''
        }`}
      >
        {badge && <Badge tone="amber">{badge}</Badge>}
        <span className="min-w-0 break-words">{v}</span>
      </dd>
    </div>
  )
}

function ChangeList({ title, changes }: { title: string; changes: FieldChange[] }) {
  if (!changes.length) return null
  return (
    <div className="px-3 py-2.5">
      <p className="mb-1.5 text-[12px] text-amber-500/80">{title}</p>
      <dl className="space-y-1.5">
        {changes.map((c) => (
          <div key={c.path} className="flex items-start justify-between gap-4 text-[12px]">
            <dt className="text-theme-text-secondary">{c.label}</dt>
            <dd className="flex min-w-0 items-baseline gap-1.5 text-right">
              {/* Dimmed rather than struck through — a line through a single digit is harder to
                  read than the digit is to ignore. */}
              <span className="text-theme-text-tertiary">{c.from}</span>
              <span className="text-theme-text-tertiary">&rarr;</span>
              <span className="break-words font-medium text-theme-text-primary">{c.to}</span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/** The settings one template resolved to, in full. */
function FullSettings({
  title,
  subtitle,
  obj,
  opts,
}: {
  title: string
  subtitle: string
  obj: unknown
  opts?: FlattenOptions
}) {
  const sections = useMemo(() => groupSettings(flattenSettings(obj, opts)), [obj, opts])
  return (
    <div className="mt-4">
      <p className={`${labelCls} mb-1`}>{title}</p>
      <p className="mb-2 text-[12px] text-theme-text-tertiary">{subtitle}</p>
      <div className="space-y-3">
        {sections.map((s) => (
          <div
            key={s.group}
            className="rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2"
          >
            <p className={`mb-1.5 ${labelCls}`}>{s.label}</p>
            <dl className="space-y-1">
              {s.leaves.map((l) => (
                <div key={l.path} className="flex items-start justify-between gap-4 text-[12px]">
                  <dt className="text-theme-text-tertiary">{l.label}</dt>
                  <dd className="min-w-0 break-words text-right font-medium text-theme-text-primary">
                    {l.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- step 4
function OverviewStep({
  apps,
  appName,
  effUx,
  uxTemplate,
  uxEdited,
  effDep,
  depTemplate,
  depEdited,
  groups,
  filters,
  depView,
  preflight,
  groupName,
  shownApp,
  onShowApp,
  editsExist,
  discardEdits,
  onDiscardEdits,
  deployment,
  deploymentLine,
  nameNote,
  clashChoice,
  onClashChoice,
  onEditNames,
  installContext,
  thisLabel,
  longNames,
  deploymentsUnread,
  onNewDeployment,
}: {
  apps: DeployApp[]
  /** The Intune app name this deployment's templates give. */
  appName: (a: DeployApp) => string
  /** The resolved user-experience settings this rollout will publish. */
  effUx: any | null
  uxTemplate: DeploymentTemplate | undefined
  uxEdited: boolean
  /** The resolved Intune delivery settings this rollout will publish. */
  effDep: any | null
  depTemplate: IntuneDeploymentTemplate | undefined
  depEdited: boolean
  groups: AssignmentGroup[]
  /** Intune assignment filters, so the review names the filter each assignment carries. */
  filters: { id: string; name: string }[]
  depView: FlattenOptions
  /** What the backend says starting this deployment does to each app. */
  preflight: PreflightView
  groupName: { app: GeneratedGroupName; update: GeneratedGroupName } | null
  shownApp: number
  onShowApp: (i: number) => void
  /** Some app object of these deployments carries an edit made on the deployment page. */
  editsExist: boolean
  discardEdits: boolean
  onDiscardEdits: (v: boolean) => void
  /** How the deployment renders its names. */
  deployment: DeploymentNaming
  /** Which deployment this creates or updates, when the package already has one. */
  deploymentLine: string | null
  /** For a new deployment: whether and why its name appears in the Intune names. */
  nameNote: NameNote | null
  /** What to do when a new deployment's names clash with another deployment's: add the
   *  deployment name to them, or change the name patterns. */
  clashChoice: ClashChoice
  onClashChoice: (c: ClashChoice) => void
  /** Opens the name pattern editor (the Deployment template's Adjust). */
  onEditNames: () => void
  /** The install context a deployment that is not published yet is created with. */
  installContext: string | null
  /** How this deployment is named in the shared-group warning. */
  thisLabel: string
  /** Starts over on the first step with a new deployment chosen; offered when an update in
   *  place renames the deployed apps or changes their groups. */
  onNewDeployment?: () => void
  /** Names longer than Intune and Entra allow. */
  longNames: string[]
  /** One package whose deployments could not be read, so the deployment choice is missing. */
  deploymentsUnread: boolean
}) {
  const dev = useDevMode()
  const [showAll, setShowAll] = useState(false)
  const app = apps[shownApp] ?? apps[0]
  /* The update policy the deployment will have. An update in place reports it from the
     preflight, which lays the values changed for that deployment over the template's. */
  const policyAfter =
    preflight.items.length === 1 ? preflight.items[0].changes?.automated_updates?.to : undefined
  const au = policyAfter ?? effDep?.automatedUpdates
  const updateRows: AssignmentRow[] = effDep?.updateOnly?.assignments ?? []
  /* Publish folds Skip install: the update-only object is then the only one created, under the
     update-only name, with the update-only rows and groups. Mirrored here, under the resolver's
     own condition, so the review does not list an install app that is never created. */
  const skipInstall = !!effDep?.skipInstall && !!effDep?.updateOnly?.enabled
  const rows: AssignmentRow[] = skipInstall ? updateRows : (effDep?.appAssignments ?? [])
  const updateEnabled = !skipInstall && !!effDep?.updateOnly?.enabled

  // Generated groups do not exist yet, so their names come from the pattern — resolved against
  // this app, which is why they sit in the per-app section.
  const nameVars = {
    appname: app?.name,
    version: app?.version,
    publisher: app?.publisher,
    arch: app?.arch,
  }
  const updateName = renderNamingPattern(
    effectiveNaming(effDep?.naming ?? {}, deployment).updateAppPattern ?? '',
    withDeploymentVars(nameVars, deployment),
  )
  const objectLabel = skipInstall ? 'Update-only app' : 'App'
  const objectName = skipInstall ? updateName || appName(app) : appName(app)
  const objectGroupName = skipInstall ? groupName?.update : groupName?.app
  const created = effDep
    ? generatedGroupNames(
        {
          naming: effDep.naming ?? {},
          appAssignments: skipInstall ? [] : rows,
          updateAssignments: skipInstall || updateEnabled ? updateRows : [],
        },
        nameVars,
        deployment,
      )
    : []
  const sharedGroups = preflight.items.flatMap((i) => i.shared_groups ?? [])

  /** Every assignment this rollout publishes, app object first. The generated targets carry the
   *  names publish will create them under, which the "Groups created" line used to restate. */
  const assignmentTable = [
    ...assignmentReviewRows(objectLabel, rows, groups, filters, objectGroupName),
    ...(updateEnabled
      ? assignmentReviewRows('Update-only', updateRows, groups, filters, groupName?.update)
      : []),
  ]

  // A template saved before this rule was enforced can still carry the pair. Shown here because
  // this is the last screen before the Graph call that would be refused outright.
  const duplicates = [
    ...duplicateTargetProblems(rows, groups, objectGroupName),
    ...(updateEnabled ? duplicateTargetProblems(updateRows, groups, groupName?.update) : []),
  ]

  // What the names above could NOT resolve. Surfaced rather than left for the admin to notice a
  // stray brace: these names are read-only once created.
  const unresolved = Array.from(
    new Set([
      ...unresolvedTokens(objectName),
      ...(updateEnabled ? unresolvedTokens(updateName) : []),
      ...created.flatMap(unresolvedTokens),
    ]),
  )

  const uxChanges = useMemo(
    () =>
      uxEdited && uxTemplate
        ? diffSettings(uxTemplate.config_json, effUx, { labels: TEMPLATE_SETTING_LABELS })
        : [],
    [uxEdited, uxTemplate, effUx],
  )
  const depChanges = useMemo(
    () =>
      depEdited && depTemplate
        ? diffSettingsComplete(depTemplate.config, effDep, depView, PATTERN_LABELS)
        : [],
    [depEdited, depTemplate, effDep, depView],
  )

  return (
    <div className={dev ? 'grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]' : ''}>
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className={labelCls}>What gets created</p>
          {/* The summary is the answer to "is this right?". Everything else is still here for
              anyone who needs to check a specific setting. */}
          <button
            type="button"
            className={btnOutlineSm}
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
          >
            {showAll ? (
              <ChevronDownIcon className="h-3.5 w-3.5" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5" />
            )}
            {showAll ? 'Summary' : 'All settings'}
          </button>
        </div>

        {/* One app at a time: the names and the Graph calls differ per package, so showing them
            merged would describe a deployment nobody asked for. */}
        {apps.length > 1 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {apps.map((a, i) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onShowApp(i)}
                className={`rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
                  i === shownApp
                    ? 'border-theme-accent-blue bg-theme-accent-blue/10 text-theme-text-primary'
                    : 'border-theme-border-primary bg-theme-tertiary text-theme-text-secondary hover:text-theme-text-primary'
                }`}
              >
                {a.name}
              </button>
            ))}
          </div>
        )}

        <dl className="space-y-2 text-sm">
          {deploymentLine && <Line k="Deployment" v={deploymentLine} />}
          {installContext && <Line k="Install context" v={installContext} />}
          <Line k={objectLabel} v={<PatternText text={objectName} />} mono />
          {updateEnabled && (
            <Line k="Update-only app" v={<PatternText text={updateName || '—'} />} mono />
          )}
          <Line k="Version" v={app?.version || '—'} mono />
        </dl>
        {nameNote &&
          (nameNote.added ? (
            <fieldset
              aria-label="Name clash"
              className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-500"
            >
              <legend className="sr-only">Name clash</legend>
              <p className="font-semibold">{nameNote.text}</p>
              <p className="mt-1 text-theme-text-secondary">
                Choose how this deployment gets names of its own. The deployment {nameNote.other}{' '}
                keeps its names.
              </p>
              <label className="mt-2 flex items-start gap-2 text-theme-text-primary">
                <input
                  type="radio"
                  name="name-clash"
                  aria-label="Add the deployment name to the names"
                  className="mt-0.5"
                  checked={clashChoice === 'add'}
                  onChange={() => onClashChoice('add')}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">Add the deployment name to the names</span>
                  <span className="block text-theme-text-secondary">
                    The deployment name is added to the end of every app and group name:
                  </span>
                  <ul className="mt-1 space-y-0.5 font-mono text-theme-text-secondary">
                    {nameNote.renamed.map((r) => (
                      <li key={r.from}>
                        {r.from} → {r.to}
                      </li>
                    ))}
                  </ul>
                </span>
              </label>
              <label className="mt-2 flex items-start gap-2 text-theme-text-primary">
                <input
                  type="radio"
                  name="name-clash"
                  aria-label="Change the names"
                  className="mt-0.5"
                  checked={clashChoice === 'change'}
                  onChange={() => onClashChoice('change')}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">Change the names</span>
                  <span className="block text-theme-text-secondary">
                    Edit this deployment's name patterns so they give names the deployment{' '}
                    {nameNote.other} does not use. Deploy is available again once they differ.
                  </span>
                  {clashChoice === 'change' && (
                    <button
                      type="button"
                      onClick={onEditNames}
                      className="mt-1.5 rounded-lg border border-theme-border-primary bg-theme-primary px-2 py-1 text-[12px] font-medium text-theme-text-primary hover:bg-theme-tertiary"
                    >
                      Edit the name patterns
                    </button>
                  )}
                </span>
              </label>
            </fieldset>
          ) : (
            <p className="mt-2 text-[12px] text-theme-text-secondary">{nameNote.text}</p>
          ))}

        {unresolved.length > 0 && (
          <p className="mt-3 text-[12px] text-theme-text-tertiary">
            {unresolved.join(', ')} {unresolved.length === 1 ? 'is' : 'are'} not known for this app
            yet and {unresolved.length === 1 ? 'is' : 'are'} resolved when it is packaged, so the
            name created in Intune will differ from the one shown here.
          </p>
        )}

        {longNames.map((n) => (
          <p
            key={n}
            className="mt-3 rounded-lg border border-red-500/40 bg-red-500/[0.07] px-3 py-2 text-[12px] text-red-400"
          >
            {n} is longer than the {INTUNE_NAME_MAX} characters Intune and Entra allow. Choose a
            shorter deployment name, or templates that give shorter names.
          </p>
        ))}

        {/* Two deployments of one package share detection rules, so a device in both audiences
            can be installed by one and uninstalled by the other. Warned about, never refused. */}
        {sharedGroups.map((g) => (
          <p
            key={`${g.group}|${g.role}|${g.other_deployment_uid ?? ''}|${g.other_role}`}
            className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-500"
          >
            {sharedGroupText(g, app?.name ?? '', thisLabel)}
          </p>
        ))}

        {duplicates.map((m) => (
          <p
            key={m}
            className="mt-3 rounded-lg border border-red-500/40 bg-red-500/[0.07] px-3 py-2 text-[12px] text-red-400"
          >
            {m} Adjust the deployment template's assignments before deploying.
          </p>
        ))}

        {/* What starting this deployment does to each app, from the backend's own decision. A
            refused item disables Deploy; an in-place item is confirmed before the job starts. */}
        {preflight.error && (
          <p className="mt-3 rounded-lg border border-red-500/40 bg-red-500/[0.07] px-3 py-2 text-[12px] text-red-400">
            Could not check the existing deployments in Intune: {preflight.error} Deploy stays
            disabled until it can.
          </p>
        )}
        {preflight.loading && (
          <p className="mt-3 text-[12px] text-theme-text-tertiary">
            Checking the existing deployments in Intune…
          </p>
        )}
        {preflight.items
          .filter((i) => i.mode === 'refused')
          .map((i) => (
            <p
              key={i.app_id}
              className="mt-3 rounded-lg border border-red-500/40 bg-red-500/[0.07] px-3 py-2 text-[12px] text-red-400"
            >
              {refusalText(i, deploymentsUnread)}
            </p>
          ))}
        {preflight.items
          .filter((i) => i.mode === 'update_in_place')
          .map((i) => (
            <div key={i.app_id} className="mt-3">
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-500">
                {i.name ? `${i.name}: ` : ''}{deployedAs(i)} Deploy applies these templates to{' '}
                {deploymentLine ? `the deployment ${thisLabel}` : 'that deployment'}; no second app
                is created. {noticeTexts(i).join(' ')}
              </p>
              {onNewDeployment && changesNames(i) && (
                <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-500">
                  These templates change the existing deployment: {changesNames(i)}. To leave it
                  as it is, create a new deployment instead.{' '}
                  <button type="button" className="underline" onClick={onNewDeployment}>
                    New deployment
                  </button>
                </p>
              )}
              <DeploymentChanges item={i} deliveryView={depView} />
            </div>
          ))}
        {preflight.items
          .filter(
            (i) =>
              i.mode !== 'update_in_place' &&
              !!(i.changes?.overrides?.kept?.length || i.changes?.overrides?.dropped?.length),
          )
          .map((i) => (
            <div key={i.app_id} className="mt-3">
              {i.name && <p className="text-[12px] font-medium text-theme-text-primary">{i.name}</p>}
              <DeploymentChanges item={i} deliveryView={depView} />
            </div>
          ))}
        {editsExist && (
          <label className="mt-3 flex items-start gap-2 text-[12px] text-theme-text-secondary">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={discardEdits}
              onChange={(e) => onDiscardEdits(e.target.checked)}
            />
            <span>
              <span className="font-medium text-theme-text-primary">
                Discard edits made on the deployment page
              </span>
              <br />
              An edit is kept while you deploy with the template that governs it. Tick this to
              apply the templates' values instead.
            </span>
          </label>
        )}
        {preflight.items
          .map(publishNote)
          .filter((n): n is string => !!n)
          .map((n) => (
            <p key={n} className="mt-3 rounded-lg border border-theme-border-primary px-3 py-2 text-[12px] text-theme-text-secondary">
              {n}
            </p>
          ))}

        {apps.length > 1 && (
          <p className="mt-2 text-[12px] text-theme-text-tertiary">
            The experience, targeting and update settings below are the same for all {apps.length}{' '}
            apps — only the names above{dev ? ' and the Graph calls' : ''} differ.
          </p>
        )}

        {/* What this rollout bent. Shown regardless of the summary/detail choice — it is the part
            of the review nobody should have to hunt for. */}
        {(uxChanges.length > 0 || depChanges.length > 0) && (
          <div className="mt-4 overflow-hidden rounded-xl border border-amber-500/30 bg-amber-500/[0.06]">
            <div className="border-b border-amber-500/20 px-3 py-2">
              <p className={`${labelCls} text-amber-500`}>Changed from the template</p>
              <p className="mt-0.5 text-[12px] text-amber-500/70">
                Applies to this deployment only — the templates keep their own values.
              </p>
            </div>
            <div className="divide-y divide-amber-500/15">
              {uxTemplate && <ChangeList title={uxTemplate.name} changes={uxChanges} />}
              {depTemplate && <ChangeList title={depTemplate.name} changes={depChanges} />}
            </div>
          </div>
        )}

        {showAll ? (
          <>
            {effUx && (
              <FullSettings
                title="User experience"
                subtitle={uxTemplate?.name ?? 'Adjusted for this deployment'}
                obj={effUx}
              />
            )}
            {effDep && (
              <FullSettings
                title="Deployment"
                subtitle={depTemplate?.name ?? 'Adjusted for this deployment'}
                obj={effDep}
                opts={depView}
              />
            )}
          </>
        ) : (
          <>
            <p className={`${labelCls} mt-4 block`}>User experience</p>
            <dl className="space-y-2 text-sm">
              <Line
                k="Template"
                v={uxTemplate?.name ?? 'None — package default'}
                badge={uxEdited ? 'Adjusted' : undefined}
              />
              <Line k="Behaviour" v={effUx ? uxLine(effUx) : '—'} />
            </dl>

            <p className={`${labelCls} mt-4 block`}>Deployment</p>
            <dl className="space-y-2 text-sm">
              <Line
                k="Template"
                v={depTemplate?.name ?? 'None — ad hoc'}
                badge={depEdited ? 'Adjusted' : undefined}
              />
            </dl>
            <div className="mt-2">
              <AssignmentReviewTable rows={assignmentTable} />
              {assignmentTable.some((r) => r.generated) && (
                <p className="mt-2 text-[12px] text-theme-text-tertiary">
                  A target marked new does not exist yet — publishing the app creates it.
                </p>
              )}
            </div>

            {au && (
              <>
                <p className={`${labelCls} mt-4 block`}>Automated updates</p>
                <dl className="space-y-2 text-sm">
                  <Line
                    k="New versions"
                    v={
                      au.followNewVersions
                        ? au.soakDays === 0
                          ? 'Published immediately'
                          : `Published ${au.soakDays} day${au.soakDays === 1 ? '' : 's'} after release`
                        : 'Manual'
                    }
                  />
                  <Line
                    k="Versions kept"
                    v={au.keepVersions === 0 ? 'Only the latest' : `${au.keepVersions ?? 3} previous`}
                  />
                </dl>
              </>
            )}
          </>
        )}
      </div>

      {/* Graph is a developer-mode aid: without it the column is an empty heading, so it does not
          exist at all. */}
      {dev && (
        <div>
          <p className={labelCls}>Graph calls</p>
          <DevNote
            setting="Publish order: groups → app → content → assignments"
            example="Each step needs the id the one before it returns, so they cannot be batched."
          />
          {created.length > 0 && (
            <DevScript title="1 · Resolve the security groups" lines={graphGroupLines(created)} />
          )}
          <DevScript
            title={created.length ? '2 · Create the app' : '1 · Create the app'}
            lines={graphCreateAppLines({
              displayName: appName(app),
              publisher: app?.publisher,
              installContext: effDep?.intuneMetadata?.installContext,
              restartBehaviour: effDep?.restartBehaviour,
              returnCodes: effDep?.returnCodes,
            })}
          />
          <DevScript
            title={created.length ? '3 · Upload the package' : '2 · Upload the package'}
            lines={graphContentLines(`${appName(app)}.intunewin`)}
          />
          <DevScript
            title={created.length ? '4 · Assign it' : '3 · Assign it'}
            lines={graphAssignLines(rows, groups, { generatedGroupName: groupName?.app })}
          />
          {updateEnabled && (
            <DevScript
              title={created.length ? '5 · Assign the update-only app' : '4 · Assign the update-only app'}
              lines={graphAssignLines(updateRows, groups, {
                generatedGroupName: groupName?.update,
                appRef: '{updateAppId}',
              })}
            />
          )}
          <p className="mt-2 text-[12px] text-theme-text-tertiary">
            Generated from the publisher's own mapping (services/intune_publisher.py). The ids are
            placeholders until the calls above return them.
          </p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- wizard
export default function DeployWizard({
  isOpen,
  onClose,
  apps,
  onStarted,
  onDeployed,
  initialTarget,
}: Props) {
  const [stepIdx, setStepIdx] = useState(0)
  /** Which steps this deployment has reached. Nothing here exists yet — a rollout is always being
   *  created — so a step nobody has walked through is not somewhere to jump to. */
  const [visited, setVisited] = useState<Set<string>>(() => new Set([STEPS[0].key]))
  const [uxId, setUxId] = useState<string | null>(null)
  const [depId, setDepId] = useState<string | null>(null)
  /** This rollout's own settings when it adjusted them; null = use the template as-is. */
  const [uxOverride, setUxOverride] = useState<any | null>(null)
  const [adjusting, setAdjusting] = useState(false)
  /** The delivery twin of uxOverride. Drives Graph for this publish only. */
  const [depOverride, setDepOverride] = useState<any | null>(null)
  const [adjustingDep, setAdjustingDep] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The in-place confirmation is open: some of these apps are already deployed at this version. */
  const [confirming, setConfirming] = useState(false)
  /** Which app the overview is showing, when several are going out together. */
  const [shownApp, setShownApp] = useState(0)
  // app id -> 'system' | 'user' | '' (leave it to the package). Per app because runAsAccount has
  // to match what that installer does.
  const [installContexts, setInstallContexts] = useState<Record<string, string>>({})
  const [discardEdits, setDiscardEdits] = useState(false)
  /** The deployment choice the pickers were preselected for. */
  const [preselectFor, setPreselectFor] = useState<string | null>(null)
  /** The administrator's choice of deployment; null until one is made on the Apps step. */
  const [chosen, setChosen] = useState<WizardTarget | null>(null)
  const [newName, setNewName] = useState('')
  /** The name of the package's first deployment, asked when the package has no deployment yet. */
  const [firstName, setFirstName] = useState(FIRST_DEPLOYMENT_LABEL)

  const qc = useQueryClient()
  const notify = useNotificationStore((s) => s.notify)
  const openTaskList = useTaskListStore((s) => s.openTaskList)

  const uxQ = useQuery({
    queryKey: ['deployment-templates'],
    queryFn: listDeploymentTemplates,
    enabled: isOpen,
  })
  const depQ = useQuery({
    queryKey: ['intune-templates'],
    queryFn: listIntuneTemplates,
    enabled: isOpen,
  })
  const uxListed = useMemo(() => uxQ.data?.templates ?? [], [uxQ.data])
  const depListed = useMemo(() => depQ.data?.templates ?? [], [depQ.data])
  /** Where the pickers opened, from the deployments' current settings; null until they settle. */
  const [preselect, setPreselect] = useState<ReturnType<typeof planPreselection> | null>(null)
  // A deleted template is offered as the deployment's own settings, never as another template.
  const uxKept = preselect?.ux.kept
  const depKept = preselect?.dep.kept
  const uxTemplates = useMemo(
    () =>
      uxKept
        ? [
            ...uxListed,
            { id: KEPT_ID, name: keptLabel(uxKept.name), config_json: uxKept.config } as DeploymentTemplate,
          ]
        : uxListed,
    [uxListed, uxKept],
  )
  const depTemplates = useMemo(
    () =>
      depKept
        ? [
            ...depListed,
            { id: KEPT_ID, name: keptLabel(depKept.name), config: depKept.config } as IntuneDeploymentTemplate,
          ]
        : depListed,
    [depListed, depKept],
  )

  const uxTemplate = uxTemplates.find((t) => t.id === uxId)
  const depTemplate = depTemplates.find((t) => t.id === depId)

  /** The settings each app's deployment uses now, read before any template is chosen. */
  const appIds = apps.map((a) => a.id)
  const currentQ = useQuery({
    queryKey: ['deploy-current-settings', appIds.join(',')],
    queryFn: () => fetchCurrentSettings(appIds),
    enabled: isOpen && appIds.length > 0,
    retry: false,
    staleTime: 0,
  })
  const currentSettled = appIds.length === 0 || currentQ.isSuccess || currentQ.isError
  const deploymentsUnread = apps.length === 1 && currentQ.isError
  /** Packages a deployment job is still publishing: their deployment is not recorded yet. */
  const publishingNames = apps
    .filter((a) => (currentQ.data?.apps ?? []).find((x) => x.app_id === a.id)?.publishing)
    .map((a) => a.name)

  /**
   * Which deployment this creates or updates. Offered for one package that already has a
   * deployment; a deploy of several packages names none, and the backend refuses a package with
   * several deployments there.
   */
  const packageName = apps[0]?.name ?? ''
  const existing: CurrentDeploymentSettings[] =
    apps.length === 1
      ? ((currentQ.data?.apps ?? []).find((a) => a.app_id === apps[0].id)?.deployments ?? [])
      : []
  /** Why this package takes no new deployment; only its existing deployments can be updated. */
  const newBlocked = apps.length === 1 ? apps[0].newDeploymentBlocked : undefined
  const target: WizardTarget | undefined = !existing.length
    ? undefined
    : ((chosen?.action === 'new' && newBlocked ? null : chosen) ??
      (initialTarget?.action === 'new' && !newBlocked
        ? { action: 'new' }
        : {
            action: 'update',
            uid: chosenDeployment(existing, initialTarget)?.deployment_uid ?? null,
          }))
  const updating = target?.action === 'update' ? chosenDeployment(existing, target) : undefined
  /** Set from the preflight: a new deployment whose templates give another deployment's names gets
   *  its name added to every name. */
  const [newNameAdded, setNewNameAdded] = useState(false)
  const [clashChoice, setClashChoice] = useState<ClashChoice>('add')
  /** How the deployment renders its names: a new one from its name and the preflight, an existing
   *  one as recorded, and the package's first deployment without a name. */
  const deploymentNaming: DeploymentNaming =
    target?.action === 'new'
      ? { name: newName.trim() || null, nameAdded: newNameAdded, updateGroupIntent: null }
      : updating
        ? {
            // {DeploymentName} renders nothing for the first deployment, whatever its name.
            name: updating.deployment_uid ? (updating.deployment_name ?? null) : null,
            nameAdded: !!updating.deployment_name_added,
            updateGroupIntent: updating.update_group_intent ?? null,
          }
        : FIRST_DEPLOYMENT
  const namingKey = JSON.stringify(deploymentNaming)
  const thisLabel =
    target?.action === 'new'
      ? newName.trim() || 'the new deployment'
      : target
        ? deploymentLabel(updating?.deployment_name, packageName)
        : deploymentLabel(firstName, packageName)
  /** One package with no deployment yet: this deploy creates its first deployment, which is
   *  named here. Not offered while its deployments are unknown or one is being published. */
  const namesFirst =
    apps.length === 1 && currentSettled && !deploymentsUnread && !publishingNames.length &&
    !existing.length
  const nameProblem =
    target?.action === 'new'
      ? deploymentNameProblem(newName, existing.map((d) => d.deployment_name))
      : namesFirst
        ? deploymentNameProblem(firstName, [], true)
        : null
  const deploymentTarget: DeploymentTarget | undefined = !target
    ? namesFirst
      ? { action: 'new', uid: null, name: firstName.trim() }
      : undefined
    : target.action === 'new'
      ? { action: 'new', uid: null, name: newName }
      : { action: 'update', uid: updating?.deployment_uid ?? target.uid, name: null }
  const targetKey = !target
    ? 'none'
    : target.action === 'new'
      ? 'new'
      : `update:${updating?.deployment_uid ?? ''}`
  const deploymentLine = !target
    ? apps.length === 1 && currentSettled && !deploymentsUnread
      ? `New deployment: ${firstName.trim() || FIRST_DEPLOYMENT_LABEL} (the package's first)`
      : null
    : target.action === 'new'
      ? `New deployment: ${thisLabel}`
      : `Update ${thisLabel}`

  /**
   * Open on what the deployment uses now. Held until the current settings settle, so the tenant
   * default never shows (or starts a preflight) first; when the read fails, the default applies.
   * Choosing another deployment, or a new one, opens the pickers again on its settings.
   */
  useEffect(() => {
    if (!isOpen || !currentSettled || !uxQ.isFetched || !depQ.isFetched) return
    if (preselect && preselectFor === targetKey) return
    const plan = planPreselection(
      currentQ.data?.apps ?? [],
      uxListed,
      depListed,
      apps.length,
      target,
    )
    setUxId(plan.ux.id)
    setDepId(plan.dep.id)
    setUxOverride(plan.ux.override)
    setDepOverride(plan.dep.override)
    setPreselect(plan)
    setPreselectFor(targetKey)
    // `target` is rebuilt every render; `targetKey` says when it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preselect, preselectFor, targetKey, currentSettled, uxQ.isFetched, depQ.isFetched, currentQ.data, uxListed, depListed, apps.length])
  const initialised = preselect !== null

  /*
   * The dialog's choices are kept for the browser session, so a sign-in after the session
   * expired (which unmounts the dialog) does not lose them: reopening the dialog for the same
   * packages restores them. The draft goes when the dialog is closed or the deploy starts.
   */
  const draftKey = `deploy-draft:${appIds.join(',')}`
  const [draft, setDraft] = useState<WizardDraft | null>(null)
  const [restoredDraft, setRestoredDraft] = useState(false)
  useEffect(() => {
    if (!isOpen || !appIds.length) return
    const saved = readDraft(draftKey)
    if (saved) {
      setDraft(saved)
      setChosen(saved.chosen)
      setNewName(saved.newName)
      if (saved.firstName !== undefined) setFirstName(saved.firstName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, draftKey])
  useEffect(() => {
    if (!draft || !initialised || preselectFor !== targetKey) return
    setUxId(draft.uxId)
    setDepId(draft.depId)
    setUxOverride(draft.uxOverride)
    setDepOverride(draft.depOverride)
    setInstallContexts(draft.installContexts)
    setVisited(new Set(STEPS.slice(0, draft.stepIdx + 1).map((s) => s.key)))
    setStepIdx(draft.stepIdx)
    setDraft(null)
    setRestoredDraft(true)
  }, [draft, initialised, preselectFor, targetKey])
  useEffect(() => {
    if (!isOpen || !initialised || draft) return
    writeDraft(draftKey, {
      at: Date.now(), chosen, newName, firstName, uxId, depId, uxOverride, depOverride,
      installContexts, stepIdx,
    })
  }, [isOpen, initialised, draft, draftKey, chosen, newName, firstName, uxId, depId, uxOverride,
      depOverride, installContexts, stepIdx])

  /** Any app object of the deployments being updated carries an edit made on the deployment
   *  page. A new deployment has none. */
  const editsExist =
    target?.action === 'new'
      ? false
      : (target ? (updating ? [updating] : []) : (currentQ.data?.apps ?? []).flatMap((a) => a.deployments ?? [])).some(
          (d) =>
            (d.objects ?? []).some((o) => Object.keys(o.edited ?? {}).length > 0) ||
            Object.values(d.automated_updates_overrides ?? {}).some((v) => v != null),
        )

  /**
   * Open on the tenant default, as the mock does.
   *
   * A picker that opens on "Choose a template…" makes an admin state a preference the tenant has
   * already stated. The default is only applied while nothing is chosen, so it never overwrites a
   * selection when the list refetches.
   *
   * Order: the tenant's own choice, then the built-in default, then the first in the list. The
   * list is sorted global-first by name, so without the middle step a tenant that has chosen
   * nothing would open on whichever built-in template sorts first, not on the one shipped as
   * the default.
   */
  useEffect(() => {
    if (initialised && uxId === null && uxTemplates.length) {
      const preferred =
        uxTemplates.find((t) => t.is_default) ?? uxTemplates.find((t) => t.built_in_default)
      setUxId((preferred ?? uxTemplates[0]).id)
    }
  }, [initialised, uxId, uxTemplates])
  useEffect(() => {
    if (initialised && depId === null && depTemplates.length) {
      const preferred =
        depTemplates.find((t) => t.is_default) ?? depTemplates.find((t) => t.built_in_default)
      setDepId((preferred ?? depTemplates[0]).id)
    }
  }, [initialised, depId, depTemplates])

  const step = STEPS[stepIdx]
  const isLast = stepIdx === STEPS.length - 1
  const rail = useStepper(stepIdx)

  // Each step starts at its own top via the Modal's `scrollKey`, exactly as the reference does it.

  /** What this deployment actually uses: the override if there is one, else the template. */
  const effUx = uxOverride ?? uxTemplate?.config_json ?? null
  const effDep = depOverride ?? depTemplate?.config ?? null
  const uxEdited = uxOverride !== null
  const depEdited = depOverride !== null

  const groupsQ = useIntuneQuery({
    queryKey: intuneKeys.adGroups(),
    queryFn: () => listAzureADGroups(),
    // Needed on every open, not only while adjusting: the review names the existing groups a
    // template targets, and the assignment blade inside Adjust has no group picker without them.
    enabled: isOpen,
  })
  const rawGroups = (groupsQ.data as any)?.groups ?? (groupsQ.data as any) ?? []
  const groups: AssignmentGroup[] = useMemo(
    () => (rawGroups as any[]).map(toAssignmentGroup),
    [rawGroups],
  )
  const filtersQ = useIntuneQuery({
    queryKey: intuneKeys.assignmentFilters(),
    queryFn: listAssignmentFilters,
    enabled: isOpen,
    retry: false,
  })
  const filters = filtersQ.data?.filters ?? []

  /** The tenant's Company Portal categories, so the adjust-this-deployment wizard files the app
   *  under a category Intune actually has. */
  const categoriesQ = useIntuneQuery({
    queryKey: intuneKeys.appCategories(),
    queryFn: listIntuneAppCategories,
    enabled: isOpen,
    retry: false,
  })

  const shown = Math.min(shownApp, Math.max(apps.length - 1, 0))
  const app = apps[shown] ?? apps[0]
  // The install context the packager detected for the app the Overview shows; the same query
  // as the install context field, so it is read once.
  const recommendedQ = useQuery({
    queryKey: ['recommended-config', app?.id],
    queryFn: () => getRecommendedConfig(app!.id),
    enabled: isOpen && !!app?.id,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
  const nameVars = useMemo(
    () => ({
      appname: app?.name,
      version: app?.version,
      publisher: app?.publisher,
      arch: app?.arch,
    }),
    [app],
  )

  const groupName = useMemo(
    () => (effDep ? groupNameBuilders(effDep.naming ?? {}, nameVars, deploymentNaming) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effDep, nameVars, namingKey],
  )

  /** The Intune displayName this deployment's templates give an app. Without a template it is the
   *  app's own name, with the deployment's name when that is added. */
  const appName = (a: DeployApp): string => {
    const pattern = effectiveNaming(effDep?.naming ?? {}, deploymentNaming).appPattern
    const added = deploymentNaming.name && deploymentNaming.nameAdded
    if (!pattern) return added ? `${a.name}-${deploymentNaming.name}` : a.name
    return (
      renderNamingPattern(
        pattern,
        withDeploymentVars(
          { appname: a.name, version: a.version, publisher: a.publisher, arch: a.arch },
          deploymentNaming,
        ),
      ) || a.name
    )
  }

  /** Names too long for Intune or Entra; Deploy is blocked on them. */
  const longNames =
    effDep && app
      ? [
          appName(app),
          ...(effDep.updateOnly?.enabled
            ? [
                renderNamingPattern(
                  effectiveNaming(effDep.naming ?? {}, deploymentNaming).updateAppPattern ?? '',
                  withDeploymentVars(nameVars, deploymentNaming),
                ),
              ]
            : []),
          ...generatedGroupNames(
            {
              naming: effDep.naming ?? {},
              appAssignments: effDep.appAssignments ?? [],
              updateAssignments: effDep.updateOnly?.enabled
                ? (effDep.updateOnly?.assignments ?? [])
                : [],
            },
            nameVars,
            deploymentNaming,
          ),
        ].filter((n) => n.length > INTUNE_NAME_MAX)
      : []


  /**
   * How the deployment template's settings should read on the review.
   *
   * Assignment arrays would otherwise render as "3 entries", and generated groups have no id to
   * look up — their names come from the pattern, filled in with THIS app rather than the editor's
   * sample one.
   */
  const depView: FlattenOptions = useMemo(() => {
    /**
     * Every describer renders the value it is HANDED, never one read from the surrounding scope.
     * `diffSettings` calls these on both sides of a comparison, so a describer that ignores its
     * argument reports the same string for the old value and the new one — and a genuine edit (a
     * naming pattern gaining an `AVD-` prefix, say) is silently discarded as a no-op.
     */
    const named = withDeploymentVars(nameVars, deploymentNaming)
    /** The pattern as this deployment renders it: with the name added when it is. */
    const own = (v: string) =>
      effectiveNaming({ appPattern: v }, deploymentNaming).appPattern ?? v
    const fill = (v: unknown) =>
      typeof v === 'string' && v ? renderNamingPattern(own(v), named) : '—'
    /** The group names a pattern produces, across the intents actually assigned. */
    const filledGroups = (v: unknown, rows: AssignmentRow[], intents: string[]) => {
      if (typeof v !== 'string' || !v) return '—'
      const used = rows.length
        ? [...new Set(rows.map((r) => INTENT_TITLE[r.intent]))]
        : intents
      return [
        ...new Set(used.map((i) => renderNamingPattern(own(v), { ...named, intent: i }))),
      ].join(', ')
    }
    return {
      describe: {
        appAssignments: (v: unknown) =>
          assignmentSummary(((v ?? []) as AssignmentRow[]), groups, groupName?.app),
        'updateOnly.assignments': (v: unknown) =>
          assignmentSummary(((v ?? []) as AssignmentRow[]), groups, groupName?.update),
        // Patterns are authored in the template editor; here they only matter filled in, because
        // this app is the one they name.
        'naming.appPattern': fill,
        'naming.updateAppPattern': fill,
        'naming.groupPattern': (v: unknown) =>
          filledGroups(v, effDep?.appAssignments ?? [], ['Required']),
        'naming.updateGroupPattern': (v: unknown) =>
          deploymentNaming.updateGroupIntent === 'UpdateOnly'
            ? filledGroups(v, [], ['UpdateOnly'])
            : filledGroups(v, effDep?.updateOnly?.assignments ?? [], ['Required']),
      },
      // Filled in, these are names — calling them patterns describes the field rather than what
      // the row shows.
      labels: {
        ...TEMPLATE_SETTING_LABELS,
        // humanize() would give "Skip Install"; the control is called "Skip install".
        skipInstall: 'Skip install',
        'naming.appPattern': 'App name',
        'naming.updateAppPattern': 'Update-only app name',
        'naming.groupPattern': 'Security groups',
        'naming.updateGroupPattern': 'Update-only groups',
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effDep, nameVars, groups, groupName, namingKey])

  const uxResolved = effUx ? uxLine(effUx) : ''
  /* With the install skipped no app object is created, so the app assignments are not the ones
     that reach a device -- summarising them would report "No assignment yet" for a rollout that
     correctly has none, and hide the audience that IS assigned. */
  const depResolved = (() => {
    if (!effDep) return ''
    const skipInstall = !!(effDep as any).skipInstall
    const rowsShown: AssignmentRow[] =
      skipInstall ? (effDep.updateOnly?.assignments ?? []) : (effDep.appAssignments ?? [])
    const name = skipInstall ? groupName?.update : groupName?.app
    if (!rowsShown.some((r: AssignmentRow) => r.mode !== 'exclude')) {
      return skipInstall
        ? 'Patches existing installs only — no assignment yet, adjust to add one'
        : 'No assignment yet — adjust to add one'
    }
    return skipInstall
      ? `Patches existing installs only — ${assignmentSummary(rowsShown, groups, name)}`
      : assignmentSummary(rowsShown, groups, name)
  })()

  /**
   * Always a template, as in the reference — there is no ad-hoc audience to state here.
   *
   * That also closes the empty-targets trap for good: `[]` is falsy in Python, so an ad-hoc
   * deployment that named nobody fell through to the handler's model defaults, which for
   * `required` is ALL DEVICES. Going through a template means the audience is always something
   * the template stated.
   */
  const delivery: Delivery = { mode: 'template', depId: sentTemplateId(depId) ?? '' }

  // Built by a pure function so the payload is covered by a parity test against the real
  // backend model, rather than only ever being exercised by clicking through the UI. Built once:
  // the preflight asks about exactly the payload Deploy then sends.
  const payload = useMemo(
    () =>
      buildDeployPayload({
        apps,
        uxId: sentTemplateId(uxId),
        // The deployment's own settings go as its configuration, as a deleted template's would.
        uxOverride: uxOverride ?? (uxId === KEPT_ID ? (uxKept?.config ?? null) : null),
        delivery,
        intuneInlineConfig: depOverride ?? (depId === KEPT_ID ? depKept?.config : undefined),
        installContexts,
        discardEdits,
        deployment: deploymentTarget,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apps, uxId, uxOverride, depId, depOverride, uxKept, depKept, installContexts, discardEdits, JSON.stringify(deploymentTarget)],
  )

  /**
   * What starting this deployment does to each app, from the backend's own decision: an app
   * whose current version is already deployed and unchanged is updated in place, a name another
   * deployment carries is refused. Read on the Apps step (the deployed install context is shown
   * there) and on the Overview, which is the answer Deploy is confirmed against.
   */
  // The changes against Intune cost two assignment reads per app, so they are asked for on the
  // Overview only, which is where they are shown.
  const includeChanges = step.key === 'overview'
  const preflightQ = useQuery({
    queryKey: ['deploy-preflight', JSON.stringify(payload), includeChanges],
    queryFn: () =>
      includeChanges
        ? preflightDeploymentJob({ ...payload, include_changes: true })
        : preflightDeploymentJob(payload),
    // On the Apps step it is asked only for the install context an update keeps; a new
    // deployment chooses its own, and its name is still being typed there.
    enabled:
      isOpen &&
      !!depId &&
      !nameProblem &&
      (step.key === 'overview' || (step.key === 'apps' && target?.action !== 'new')),
    retry: false,
    staleTime: 0,
  })
  const preflightItems = preflightQ.data?.items ?? []
  const firstItem = preflightItems.find((i) => i.app_id === apps[0]?.id)
  useEffect(() => {
    if (target?.action === 'new' && preflightQ.data) {
      setNewNameAdded(!!firstItem?.deployment_name_added)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preflightQ.data, target?.action])
  /** Whether and why a new deployment's name appears in its Intune names. */
  const nameNote: NameNote | null =
    target?.action !== 'new' || !effDep
      ? null
      : firstItem?.deployment_name_added
        ? {
            text: `These templates give the same Intune names as the deployment ${deploymentLabel(
              firstItem.name_clash?.[0]?.other_deployment_name,
            )}`,
            added: true,
            renamed: (firstItem.name_clash ?? []).map((c) => ({
              from: c.name,
              to: `${c.name}-${newName.trim()}`,
            })),
            other: deploymentLabel(firstItem.name_clash?.[0]?.other_deployment_name),
          }
        : {
            text: Object.values(effDep.naming ?? {}).some((p) => usesDeploymentName(p as string))
              ? 'Your templates use {DeploymentName}, so the deployment name appears where they place it.'
              : "The deployment name is shown in Agentic Packr only: your templates don't use {DeploymentName}, and their names differ from this package's other deployments.",
            added: false,
            renamed: [],
            other: '',
          }
  const inPlaceItems = preflightItems.filter((i) => i.mode === 'update_in_place')
  const refusedItems = preflightItems.filter((i) => i.mode === 'refused')
  /** Apps whose install context is fixed by an existing deployment: those updated in place, and
   *  those refused because the chosen context differs from the deployed one. */
  const deployedByApp = useMemo(
    () =>
      Object.fromEntries(
        preflightItems
          .filter(
            (i) =>
              target?.action !== 'new' &&
              !!i.install_context &&
              (i.mode === 'update_in_place' ||
                (i.mode === 'refused' && i.reason === 'install_context')),
          )
          .map((i) => [i.app_id, i]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preflightQ.data, target?.action],
  )
  // A context chosen before the check answered goes back to the deployed one, which is the only
  // context the deployment can be updated with.
  useEffect(() => {
    const changes = Object.entries(deployedByApp).filter(
      ([id, i]) => !!installContexts[id] && installContexts[id] !== i.install_context,
    )
    if (changes.length) {
      setInstallContexts((m) => ({
        ...m,
        ...Object.fromEntries(changes.map(([id, i]) => [id, i.install_context as string])),
      }))
    }
  }, [deployedByApp, installContexts])

  // Read from the cache only: whether an app has a deployment is known when the deployments list
  // has been read this session, and is otherwise treated as possible.
  const deploymentsQ = useIntuneQuery({
    queryKey: intuneKeys.deployments(),
    queryFn: listDeployments,
    enabled: false,
  })
  const mayBeDeployed = (appId: string) => {
    const rows = deploymentsQ.data?.deployments
    if (!rows) return true
    const bare = appId.replace(/^tenant-/, '')
    return rows.some((d) => d.app_id === appId || d.app_id === bare)
  }
  /** The preflight has not answered for the current choices yet. Not pending when it cannot run:
   *  it failed, or there is no deployment template to ask about. */
  const preflightPending =
    !preflightQ.isError &&
    !(depQ.isSuccess && depTemplates.length === 0) &&
    (!preflightQ.data || preflightQ.isFetching)
  // Apps the preflight has answered for at least once in this wizard session. A re-check (the
  // key includes the chosen install contexts) keeps the field usable: the previous answer said
  // the app has no deployment, and the choice being re-checked is the user's own.
  const [answeredApps, setAnsweredApps] = useState<Set<string>>(() => new Set())
  // Another deployment, or a new one, has not been checked yet.
  useEffect(() => setAnsweredApps(new Set()), [targetKey])
  useEffect(() => {
    if (!preflightQ.data) return
    setAnsweredApps((prev) =>
      apps.every((a) => prev.has(a.id)) ? prev : new Set([...prev, ...apps.map((a) => a.id)]),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preflightQ.data])
  const checkingDeployment = (appId: string) =>
    target?.action !== 'new' && preflightPending && mayBeDeployed(appId) && !answeredApps.has(appId)
  const preflightView: PreflightView = {
    items: preflightItems,
    loading: preflightQ.isFetching,
    error: preflightQ.isError ? errorText(preflightQ.error, 'the request failed.') : null,
  }
  /** Deploy waits for the preflight and stops on a refusal; both are explained on the Overview. */
  const deployBlocked =
    submitting ||
    !preflightQ.data ||
    preflightQ.isFetching ||
    refusedItems.length > 0 ||
    !!nameProblem ||
    longNames.length > 0 ||
    (!!nameNote?.added && clashChoice === 'change')

  const reset = () => {
    setStepIdx(0)
    setVisited(new Set([STEPS[0].key]))
    setUxId(null)
    setDepId(null)
    setUxOverride(null)
    setDepOverride(null)
    setAdjusting(false)
    setAdjustingDep(false)
    setShownApp(0)
    setSubmitting(false)
    setConfirming(false)
    setError(null)
    setAnsweredApps(new Set())
    setDiscardEdits(false)
    setPreselect(null)
    setPreselectFor(null)
    setChosen(null)
    setNewName('')
    setFirstName(FIRST_DEPLOYMENT_LABEL)
    setClashChoice('add')
    setDraft(null)
    setRestoredDraft(false)
    clearDraft(`deploy-draft:${apps.map((a) => a.id).join(',')}`)
    // The next open reads the deployments again: a deploy or an edit may have changed them.
    qc.removeQueries({ queryKey: ['deploy-current-settings'] })
  }
  const close = () => {
    reset()
    onClose()
  }

  /** Why the wizard will not move on from `key`, or null. */
  const stepBlocker = (key: string): string | null => {
    if (key === 'apps' && !currentSettled) return "Checking this package's deployments…"
    if (key === 'apps' && deploymentsUnread)
      return "This package's deployments could not be read. Try again."
    if (key === 'apps' && publishingNames.length)
      return `A deployment of ${publishingNames.join(', ')} is still being published. Wait until it finishes.`
    if (key === 'apps' && nameProblem) return nameProblem
    if (key === 'experience' && !effUx) return 'Pick a user experience'
    if (key === 'deployment') {
      if (!effDep) return 'Pick a deployment template'
      const included = (effDep.appAssignments ?? []).filter(
        (r: AssignmentRow) => r.mode !== 'exclude',
      )
      if (!included.length) return 'This template has no assignment — adjust it first'
    }
    return null
  }

  const goToStep = (i: number) => {
    if (i === stepIdx) return
    // Going back never validates, exactly as Back does not. Going forward runs the same rule
    // Continue runs, so a jump cannot skip what the step being left owns.
    if (i > stepIdx) {
      const why = stepBlocker(step.key)
      if (why) return setError(why)
    }
    setError(null)
    setVisited((v) => new Set(v).add(STEPS[i].key))
    setStepIdx(i)
  }

  const next = () => goToStep(Math.min(stepIdx + 1, STEPS.length - 1))

  /** Deploy: an in-place update is confirmed first; everything else starts at once. */
  const deploy = () => {
    if (inPlaceItems.length) {
      setConfirming(true)
      return
    }
    void startJob()
  }

  const startJob = async () => {
    setConfirming(false)
    setSubmitting(true)
    setError(null)

    // A JOB, not a publish in this request. Publishing an app is a create, an upload, a commit and
    // minutes of polling; a dozen of them cannot ride an HTTP request, and one that dies half-way
    // leaves applications in the tenant that nothing recorded.
    let job: DeploymentJob
    try {
      job = await startDeploymentJob(payload)
    } catch (e: any) {
      // Nothing was recorded, so nothing is under way: this belongs in the wizard, where the
      // settings that produced it are still on screen and can be corrected.
      setError(errorText(e, 'Deploy failed.'))
      setSubmitting(false)
      return
    }

    // Recorded and owned by a worker, so the wizard is done. Holding it open for the minutes the
    // job runs blocks the console on work nothing here contributes to; the banner follows it
    // instead, and outlives both this dialog and the page that opened it. The run is watched
    // from the header's task list, so the page this was opened on stays where it is.
    notify({
      tone: 'info',
      message:
        apps.length === 1 ? `Deploying ${apps[0].name}…` : `Deploying ${apps.length} apps…`,
      actionLabel: 'View progress',
      onAction: openTaskList,
      // The deployment lists are re-read when the run finishes; see useDeploymentJobsQuery.
    })
    // Closed before the caller is told, so that a caller which drops the selection this wizard
    // is rendering cannot re-render it against an empty list on the way out.
    close()
    onStarted?.(job)

    // Followed to the end with the wizard already gone. A job that finished is not a job that
    // worked — items fail one by one and the job still reports `completed` — so the outcome is
    // reported either way, and a failure stays up until it is read.
    try {
      const r = await awaitDeploymentJob(job.id)
      if (r.failed > 0) {
        const firstFailure = r.results.find((x) => x.status !== 'ok')
        const reason = firstFailure?.detail ? ` ${firstFailure.detail}` : ''
        notify({
          tone: 'error',
          message:
            r.succeeded === 0
              ? `Nothing was deployed.${reason}`
              : `${r.succeeded} of ${r.total} deployed, ${r.failed} failed.${reason}`,
          autoDismissMs: null,
          actionLabel: 'View details',
          onAction: openTaskList,
        })
      } else {
        notify({
          tone: 'success',
          message:
            r.total === 1
              ? `${apps[0].name} deployed to Intune`
              : `${r.total} apps deployed to Intune`,
          actionLabel: 'View details',
          onAction: openTaskList,
        })
      }
      onDeployed?.(r)
    } catch (e: any) {
      // Signed out or switched tenant: the outcome is in that tenant's task list, not this session's.
      if (e instanceof DeploymentFollowEnded) return
      notify({
        tone: 'error',
        message: errorText(e, 'Lost track of the deployment. Check Intune before retrying.'),
        autoDismissMs: null,
        actionLabel: 'View details',
        onAction: openTaskList,
      })
    }
  }

  if (!isOpen) return null

  /**
   * Apply mode, as a modal OVER the wizard rather than in place of it.
   *
   * Adjust is a detour, not a next step: you go and change some settings and come straight back to
   * the deploy you were part-way through. Replacing the content made that look like a navigation,
   * as though the half-filled deploy had been abandoned — a modal leaves it visibly waiting
   * underneath. This is only safe because the wizard itself is no longer a dialog: one layer, one
   * Escape target, one Cancel.
   *
   * `full` width because the editor puts a live PSADT preview beside the form, which is precisely
   * what got squeezed when these wizards were modals at the mock's widths.
   */
  const adjustModal = (
    <Modal
      open={adjusting && !!uxTemplate}
      onClose={() => setAdjusting(false)}
      title={uxTemplate ? `Adjust “${uxTemplate.name}” for this deployment` : 'Adjust'}
      size="full"
    >
      {uxTemplate && (
        <>
          <p className={`mb-4 ${noteCls}`}>
            Changes apply to this deployment only. <strong>{uxTemplate.name}</strong> is not
            modified, and every other app using it keeps its current settings.
          </p>
          <UxTemplateWizard
            template={{ ...uxTemplate, config_json: uxOverride ?? uxTemplate.config_json }}
            sampleApp={
              app ? { title: [app.name, app.version].filter(Boolean).join(' ') } : undefined
            }
            onClose={() => setAdjusting(false)}
            onSaved={() => setAdjusting(false)}
            onApply={(config) => {
              // Walking through the editor and out again is not an adjustment (spec §7a.1): with
              // no visible difference there is nothing to record, and an "Adjusted" badge over an
              // identical set of settings is a warning about nothing.
              setUxOverride(diffSettings(uxTemplate.config_json, config).length ? config : null)
              setAdjusting(false)
            }}
          />
        </>
      )}
    </Modal>
  )

  return (
    <>
      <Modal
        open
        onClose={close}
        scrollKey={stepIdx}
        // The reference's own widths: the Overview carries a second column in developer mode and
        // gets the wider frame, every other step is the narrower one.
        size={step.key === 'overview' ? 'xl' : 'wide'}
        title={apps.length === 1 ? `Deploy ${apps[0]?.name ?? ''}` : `Deploy ${apps.length} apps`}
        footer={
          <>
            {error && <p className="mr-auto text-sm text-red-500">{error}</p>}
            {/* Cancel stays on every step, as in the reference. Folding it into Back meant the
                only way out of step 3 was to walk back to step 1. */}
            <button type="button" className={btnOutline} onClick={close} disabled={submitting}>
              Cancel
            </button>
            {stepIdx > 0 && (
              <button
                type="button"
                className={btnOutline}
                onClick={() => goToStep(stepIdx - 1)}
                disabled={submitting}
              >
                Back
              </button>
            )}
            {isLast ? (
              <button
                type="button"
                className={btnPrimary}
                onClick={deploy}
                disabled={deployBlocked}
                title={
                  refusedItems.length
                    ? refusalText(refusedItems[0], deploymentsUnread)
                    : nameNote?.added && clashChoice === 'change'
                      ? 'Change the name patterns, or choose to add the deployment name to the names'
                      : undefined
                }
              >
                {submitting ? 'Deploying…' : 'Deploy'}
              </button>
            ) : (
              <button type="button" className={btnPrimary} onClick={next}>
                Continue
              </button>
            )}
          </>
        }
      >
      {apps.some((a) => a.notFullyTested) && (
        <div className="mb-4 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          <span className="font-semibold">Not fully tested.</span>{' '}
          {apps.filter((a) => a.notFullyTested).map((a) => a.name).join(', ')}{' '}
          was published without a passing test. Make sure the devices meet what its test could not.
        </div>
      )}
      <Stepper
        id={rail.id}
        steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
        current={stepIdx}
        onGoTo={goToStep}
        canGoTo={(i) => visited.has(STEPS[i].key)}
        ariaLabel="Deployment steps"
      />

      <div {...rail.panelProps}>
      {/* On whichever step the restored draft opens. */}
      {restoredDraft && (
        <p className="mb-3 rounded-lg border border-blue-500/30 bg-blue-500/[0.08] px-3 py-2 text-[12px] text-theme-text-primary">
          Your choices from before you signed in again were restored.
        </p>
      )}
      {step.key === 'apps' && (
        <AppsStep
          apps={apps}
          installContexts={installContexts}
          onInstallContextChange={(id, v) =>
            setInstallContexts((m) => ({ ...m, [id]: v }))
          }
          deployed={deployedByApp}
          checking={(id) => !currentSettled || checkingDeployment(id)}
          recordedContext={
            updating?.install_context ? { [apps[0]?.id]: updating.install_context } : {}
          }
          choice={
            !currentSettled ? (
              <p className={`mb-3 flex items-center gap-2 ${noteCls}`}>
                <Spinner className="h-4 w-4" /> Checking this package&apos;s deployments…
              </p>
            ) : deploymentsUnread ? (
              <p className="mb-3 rounded-lg border border-red-500/30 bg-red-500/[0.07] px-3 py-2 text-[12px] text-red-400">
                This package&apos;s deployments could not be read, so it is not known whether
                this deploy updates one or creates a new one.{' '}
                <button type="button" className="underline" onClick={() => currentQ.refetch()}>
                  Try again
                </button>
              </p>
            ) : publishingNames.length ? (
              <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-500">
                A deployment of {publishingNames.join(', ')} is still being published. Wait until
                it finishes, then choose whether to update it or create a new deployment.{' '}
                <button type="button" className="underline" onClick={() => currentQ.refetch()}>
                  Check again
                </button>
              </p>
            ) : target ? (
              <DeploymentChoice
                packageName={packageName}
                deployments={existing}
                target={target}
                onTarget={(t) => {
                  setChosen(t)
                  setError(null)
                }}
                newName={newName}
                onNewName={setNewName}
                nameProblem={nameProblem}
                newBlocked={newBlocked}
              />
            ) : namesFirst ? (
              <FirstDeploymentName name={firstName} onName={setFirstName} problem={nameProblem} />
            ) : null
          }
        />
      )}

      {step.key === 'experience' && (
        <PickStep
          label="User experience"
          blurb="What the person at the keyboard sees while this installs."
          templates={uxTemplates}
          selectedId={uxId}
          onSelect={(id) => {
            setUxId(id || null)
            // The override belonged to the template it was adjusted FROM; keeping it against a
            // different template would silently deploy settings the admin never saw.
            setUxOverride(null)
          }}
          resolved={uxResolved}
          edited={uxEdited}
          currentId={preselect?.ux.currentId}
          notice={preselect?.ux.note}
          onAdjust={() => setAdjusting(true)}
          onReset={() => setUxOverride(null)}
          devSetting="win32LobApp.installCommandLine — behaviour rides on the install command (spec §3)"
        />
      )}

      {step.key === 'deployment' && (
        <PickStep
          label="Deployment"
          blurb="Who gets it, how Intune delivers it, and what happens on the next version."
          templates={depTemplates}
          selectedId={depId}
          onSelect={(id) => {
            setDepId(id || null)
            // The override belonged to the template it was adjusted FROM; keeping it against a
            // different one would publish settings the admin never saw.
            setDepOverride(null)
          }}
          resolved={depResolved}
          edited={depEdited}
          currentId={preselect?.dep.currentId}
          notice={preselect?.dep.note}
          onAdjust={() => setAdjustingDep(true)}
          onReset={() => setDepOverride(null)}
          devSetting="POST /deviceAppManagement/mobileApps/{appId}/assignments + POST /groups for generated targets"
        />
      )}

      {step.key === 'overview' && (
        <OverviewStep
          apps={apps}
          appName={appName}
          effUx={effUx}
          uxTemplate={uxTemplate}
          uxEdited={uxEdited}
          effDep={effDep}
          depTemplate={depTemplate}
          depEdited={depEdited}
          groups={groups}
          filters={filters}
          depView={depView}
          preflight={preflightView}
          groupName={groupName}
          shownApp={shown}
          onShowApp={setShownApp}
          editsExist={editsExist}
          discardEdits={discardEdits}
          onDiscardEdits={setDiscardEdits}
          deployment={deploymentNaming}
          deploymentLine={deploymentLine}
          nameNote={nameNote}
          clashChoice={clashChoice}
          onClashChoice={setClashChoice}
          onEditNames={() => {
            goToStep(STEPS.findIndex((s) => s.key === 'deployment'))
            setAdjustingDep(true)
          }}
          installContext={
            target?.action === 'new' || !existing.length
              ? overviewContextLabel(
                  installContexts[app?.id] ?? null,
                  recommendedQ.data?.baseline?.install_scope ?? null,
                )
              : null
          }
          thisLabel={thisLabel}
          longNames={longNames}
          deploymentsUnread={deploymentsUnread}
          onNewDeployment={
            apps.length === 1 && target?.action === 'update' && !newBlocked
              ? () => {
                  setChosen({ action: 'new' })
                  goToStep(0)
                }
              : undefined
          }
        />
      )}
      </div>

      </Modal>

      {adjustModal}

      {/* The in-place confirmation. Some of these apps are already deployed at this version with
          the package unchanged, so Deploy applies the templates to those Intune apps instead of
          creating second ones; the administrator confirms that, with everything the update will
          not do said here first. */}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={target ? `Apply to the deployment ${thisLabel}?` : 'Apply to the existing deployment?'}
        footer={
          <>
            <button type="button" className={btnOutline} onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button type="button" className={btnPrimary} onClick={() => void startJob()}>
              Apply and deploy
            </button>
          </>
        }
      >
        <ul className="space-y-3">
          {inPlaceItems.map((i) => (
            <li key={i.app_id} className="rounded-lg border border-theme-border-primary px-4 py-3 text-sm">
              <p className="font-medium text-theme-text-primary">{i.name ?? i.app_id}</p>
              <p className="mt-1 text-theme-text-secondary">
                {deployedAs(i)}{' '}
                {target
                  ? `Apply this template to the deployment ${thisLabel}?`
                  : 'Apply this template to that deployment?'}
              </p>
              {noticeTexts(i).map((n) => (
                <p key={n} className="mt-1 text-[12px] text-amber-500">{n}</p>
              ))}
              <DeploymentChanges item={i} deliveryView={depView} />
            </li>
          ))}
          {preflightItems
            .filter((i) => i.mode === 'publish')
            .map((i) => (
              <li key={i.app_id} className="rounded-lg border border-theme-border-primary px-4 py-3 text-sm">
                <p className="font-medium text-theme-text-primary">{i.name ?? i.app_id}</p>
                <p className="mt-1 text-theme-text-secondary">
                  {publishNote(i) ?? 'Published as a new app.'}
                </p>
              </li>
            ))}
        </ul>
      </Modal>

      {/* Same shape as the UX adjust — a modal over the wizard, so the deploy stays visible
          underneath and the detour is obviously a detour. */}
      <Modal
        open={adjustingDep && !!depTemplate}
        onClose={() => setAdjustingDep(false)}
        title={depTemplate ? `Adjust “${depTemplate.name}” for this deployment` : 'Adjust'}
        size="full"
      >
        {depTemplate && (
          <>
            <p className={`mb-4 ${noteCls}`}>
              Changes apply to this deployment only. <strong>{depTemplate.name}</strong> is not
              modified, and every other app using it keeps its current settings. What this rollout
              went out with is recorded against the published app.
            </p>
            <DeploymentTemplateWizard
              template={{ ...depTemplate, config: depOverride ?? depTemplate.config }}
              /* Without these the assignment blade here had no group list and no filters: the
                 group picker showed only its placeholder and the filter control hid itself
                 entirely, so adjusting a rollout could not pick an existing group or set a
                 filter at all. Both are already loaded on this screen. */
              groups={groups}
              filters={filters}
              categories={(categoriesQ.data?.categories ?? []).map((c) => c.name)}
              categoriesAvailable={!categoriesQ.isError && (categoriesQ.data?.available ?? true)}
              categoriesLoading={categoriesQ.isLoading}
              previewValues={nameVars}
              onClose={() => setAdjustingDep(false)}
              onSaved={() => setAdjustingDep(false)}
              onApply={(config) => {
                setDepOverride(
                  diffSettings(depTemplate.config, config, depView).length ? config : null,
                )
                setAdjustingDep(false)
              }}
            />
          </>
        )}
      </Modal>
    </>
  )
}
